import {
  JobAvailabilityType,
  ParentSignupVerticalTypeId,
  SavedSearch,
  SavedSearchService,
} from 'us-web-services';
import { getAvailabilityTypeId } from 'us-react-components/dist/utils/booking';

import { FamilyRegistrationStore } from '../models';
import { buildNuxSavedSearchFilters } from './NuxSearchFilters';

const DEFAULT_NOTIFICATION_TYPE = {
  id: 3,
  label: 'Daily',
};
const DEFAULT_SEARCH_DISTANCE = 25;

export const getSearchType = (type?: string): string => {
  switch (type) {
    case JobAvailabilityType.partTime:
      return 'Part-time';
    case JobAvailabilityType.fullTime:
      return 'Full-time';
    default:
      return '';
  }
};

export const createSavedSearch = async (familyRegistration: FamilyRegistrationStore): Promise<SavedSearch|false> => {
  const address = familyRegistration.profile?.address;
  const availabilityLabel = getSearchType(familyRegistration.availabilityType) ?? getSearchType(JobAvailabilityType.partTime);

  if (!address) {
    throw new Error('Address is required.');
  }

  if (!familyRegistration.availabilityType ||
    ![JobAvailabilityType.partTime, JobAvailabilityType.fullTime].includes(familyRegistration.availabilityType)) {
    console.warn('Skipping saved search save, occasional or invalid availability type', familyRegistration.availabilityType);

    return Promise.resolve(false);
  }

  const availabilityTypeId = getAvailabilityTypeId(familyRegistration.availabilityType);

  if (!availabilityTypeId) {
    console.warn('Could not determine availabilityTypeId', familyRegistration.availabilityType);

    return Promise.resolve(false);
  }

  const savedSearch: SavedSearch = {
    id: familyRegistration.savedSearchId ?? undefined,
    distance: DEFAULT_SEARCH_DISTANCE,
    verticalTypeId: familyRegistration.lookingForId,
    type: {
      id: availabilityTypeId,
      label: availabilityLabel,
    },
    notificationType: DEFAULT_NOTIFICATION_TYPE,
    title: `${availabilityLabel} Search`,
    active: true,
    parentId: familyRegistration.profile.id,
    address: {
      city: address.city,
      state: address.state,
      zip: address.zip,
      street: address?.street,
      latitude: address?.latitude,
      longitude: address?.longitude,
    },
    numberOfChildren: familyRegistration.countDependents ?? 1,
    willSitDog: familyRegistration.signupVerticalTypeId === ParentSignupVerticalTypeId.PET_CARE_DOG || undefined,
    willSitCat: familyRegistration.signupVerticalTypeId === ParentSignupVerticalTypeId.PET_CARE_CAT || undefined,
    willSitOtherPet: familyRegistration.signupVerticalTypeId === ParentSignupVerticalTypeId.PET_CARE_OTHER || undefined,
    willSitSenior: familyRegistration.signupVerticalTypeId === ParentSignupVerticalTypeId.SENIOR_CARE || undefined,
    willSitHome: familyRegistration.signupVerticalTypeId === ParentSignupVerticalTypeId.HOUSEHOLD_SERVICES || undefined,
    exactRecurringAvailability: false,
    ...buildNuxSavedSearchFilters(familyRegistration),
  };

  const response = savedSearch.id
    ? (await SavedSearchService.updateSearch(savedSearch.id, savedSearch)).data
    : (await SavedSearchService.saveSearch(savedSearch)).data;

  familyRegistration.setSavedSearchId(response.data.id);
  familyRegistration.setCreatedOn(response.data.created);

  return response.data;
};
